import React from "react";
import { debounceFn } from "@/utils";
import Constants from "@/constants";
import { pageviewDownload } from "@/report";
import images from "@/assets/images";
export const useDebounceFn = (
  callback: (...args: any[]) => any,
  delay: number,
  deps: any[] = [],
) => {
  return React.useCallback(debounceFn(callback, delay), deps);
};

/**
 * @description 自动滚动定位当前选中(active)的节点
 */
export const useActiveScroll = ({
  activeIndex,
  wrapRef,
  waisMS,
  activeClassName,
}: {
  wrapRef: React.RefObject<HTMLUListElement | HTMLDivElement>;
  activeIndex: number;
  activeClassName: string;
  waisMS?: number;
}) => {
  React.useLayoutEffect(() => {
    const timer = setTimeout(
      () => {
        if (activeIndex === -1) return;
        if (!wrapRef.current) return console.warn("未找到对应的容器节点!");

        const $wrapDom = wrapRef.current;
        const $selectedDom = document.querySelector(activeClassName) as HTMLLIElement;

        if (!$selectedDom) {
          return console.warn("未找到对应的active dom节点!");
        }
        const scrollX = Math.max($selectedDom.offsetLeft - $selectedDom.offsetWidth, 0);
        if (!$wrapDom.scrollTo) {
          $wrapDom.scrollLeft = scrollX;
        } else {
          $wrapDom.scrollTo({ behavior: "smooth", left: scrollX });
        }
      },
      waisMS === undefined ? 50 : waisMS,
    );
    return () => clearTimeout(timer);
  }, [activeIndex]);
};

export const useCurTab = (): [Constants.ETabKey, (tab: Constants.ETabKey) => void] => {
  const [curTab, setCurTab] = React.useState(Constants.ETabKey.CLIENT_APP);
  const { addAnimationTime } = useAnimationTime();

  React.useEffect(() => {
    const nav = window.location.hash.replace("#", "");
    if (Constants.NAV_TAGS[Constants.ETabKey.LIVE_APP].includes(nav)) {
      setCurTab(Constants.ETabKey.LIVE_APP);
    }
  }, []);
  const reportKeyNum = React.useRef({
    [Constants.ETabKey.CLIENT_APP]: 0,
    [Constants.ETabKey.LIVE_APP]: 0,
  });
  React.useEffect(() => {
    if (reportKeyNum.current[curTab] === 0) {
      reportKeyNum.current[curTab] += 1;
      pageviewDownload({
        tab: curTab === Constants.ETabKey.CLIENT_APP ? "0" : "1",
      });
    }
  }, [curTab]);
  const handleSetCurTab = (tab: Constants.ETabKey) => {
    if (tab === curTab) return;
    setCurTab(tab);
    addAnimationTime();
    window.location.hash = `#${Constants.NAV_TAGS[tab][0]}`;
  };
  return [curTab, handleSetCurTab];
};

export const useLoadingImg = ({ isApp }: { isApp: boolean }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const appImgList = [
    images.bg,
    images.App41,
    images.App42,
    images.hyLogo,
    images.PC1,
    images.PC2,
    images.qrcode,
    images.PC3,
    images.AppLive,
  ];
  const webImgList = [
    images.bg,
    images.hyLogo,
    images.PC1,
    images.PC2,
    images.qrcode,
    images.PC3,
    images.PC41,
    images.PC42,
    images.PCLive,
    images.qrcode_live,
  ];
  // 预加载图片
  React.useEffect(() => {
    const imgList = isApp ? appImgList : webImgList;
    let loadedCount = 0;
    let time = Date.now();
    imgList.forEach(src => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        loadedCount += 1;
        if (loadedCount === imgList.length) {
          setIsLoading(false);
          console.log("所有图片加载完成，耗时:", Date.now() - time, "ms");
        }
      };
      img.onerror = () => {
        loadedCount += 1;
        if (loadedCount === imgList.length) {
          setIsLoading(false);
          console.log("所有图片加载完成，耗时:", Date.now() - time, "ms");
        }
      };
    });
  }, [isApp]);
  return isLoading;
};

let animationTime = 1;
export const useAnimationTime = () => {
  const addAnimationTime = () => {
    animationTime += 1;
  };
  const isAnimationFirst = animationTime === 1;
  return { animationTime, addAnimationTime, isAnimationFirst };
};
